"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/edgeFunctionClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  Play, 
  Send, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  Loader2,
  Eye
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EmailPreviewModal } from "./EmailPreviewModal";

export function RFQDigestTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [testEmail, setTestEmail] = useState("");
  const [dateOverride, setDateOverride] = useState<Date | undefined>();
  const [showPreview, setShowPreview] = useState(false);

  // Fetch digest enabled setting
  const { data: digestEnabled, isLoading: settingLoading } = useQuery({
    queryKey: ['platform-setting', 'daily_rfq_digest_enabled'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('setting_value')
        .eq('setting_key', 'daily_rfq_digest_enabled')
        .maybeSingle();
      
      if (error) throw error;
      return data?.setting_value === 'true';
    }
  });

  // Fetch last run info
  const { data: lastRun, isLoading: lastRunLoading } = useQuery({
    queryKey: ['mass-communication-logs', 'rfq_digest', 'latest'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mass_communication_logs')
        .select('*')
        .eq('communication_type', 'rfq_digest')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  // Toggle digest enabled
  const toggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from('platform_settings')
        .update({ setting_value: enabled ? 'true' : 'false', updated_at: new Date().toISOString() })
        .eq('setting_key', 'daily_rfq_digest_enabled');
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-setting', 'daily_rfq_digest_enabled'] });
      toast({
        title: "Setting updated",
        description: `Daily RFQ digest has been ${digestEnabled ? 'disabled' : 'enabled'}.`
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Send test email
  const testEmailMutation = useMutation({
    mutationFn: async () => {
      if (!testEmail) throw new Error("Please enter a vendor email address");
      
      const { data, error } = await invokeEdgeFunction('daily-rfq-digest', {
        body: {
          testEmail,
          dateOverride: dateOverride ? format(dateOverride, 'yyyy-MM-dd') : undefined,
          manualTrigger: true
        },
        callerOperation: 'RFQDigestTab.testEmail',
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const result = data as { emails_queued?: number } | undefined;
      toast({
        title: "Test email sent",
        description: `Digest sent to ${testEmail}. ${result?.emails_queued || 0} email(s) queued.`
      });
      queryClient.invalidateQueries({ queryKey: ['mass-communication-logs'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error sending test email",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Run full digest
  const runDigestMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await invokeEdgeFunction('daily-rfq-digest', {
        body: {
          manualTrigger: true,
          dateOverride: dateOverride ? format(dateOverride, 'yyyy-MM-dd') : undefined
        },
        callerOperation: 'RFQDigestTab.runDigest',
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const result = data as { emails_queued?: number; rfqs_found?: number } | undefined;
      toast({
        title: "Digest complete",
        description: `Sent to ${result?.emails_queued || 0} vendors. ${result?.rfqs_found || 0} RFQs found.`
      });
      queryClient.invalidateQueries({ queryKey: ['mass-communication-logs'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error running digest",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Daily RFQ Digest</span>
            <Badge variant={digestEnabled ? "default" : "secondary"}>
              {digestEnabled ? "Enabled" : "Disabled"}
            </Badge>
          </CardTitle>
          <CardDescription>
            Automated daily email digest of new RFQs sent to verified vendors at 5:00 AM ET
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Enable Digest</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, vendors receive daily RFQ digest emails
              </p>
            </div>
            <Switch
              checked={digestEnabled}
              onCheckedChange={(checked) => toggleMutation.mutate(checked)}
              disabled={settingLoading || toggleMutation.isPending}
            />
          </div>

          {lastRun && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Last Run: {format(new Date(lastRun.created_at), 'MMM d, yyyy h:mm a')}
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Recipients:</span>
                  <span className="ml-2 font-medium">{lastRun.total_recipients}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Sent:</span>
                  <span className="ml-2 font-medium text-green-600">{lastRun.successful_sends}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Failed:</span>
                  <span className="ml-2 font-medium text-destructive">{lastRun.failed_sends}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Email Card */}
      <Card>
        <CardHeader>
          <CardTitle>Test Email</CardTitle>
          <CardDescription>
            Send a test digest to a specific vendor email address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="test-email">Vendor Email</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="vendor@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Date Override (Optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateOverride && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateOverride ? format(dateOverride, 'MMM d, yyyy') : "Use yesterday"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateOverride}
                    onSelect={setDateOverride}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Button 
            onClick={() => testEmailMutation.mutate()}
            disabled={!testEmail || testEmailMutation.isPending}
          >
            {testEmailMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send Test
          </Button>
        </CardContent>
      </Card>

      {/* Manual Execution Card */}
      <Card>
        <CardHeader>
          <CardTitle>Manual Execution</CardTitle>
          <CardDescription>
            Trigger the digest manually for all eligible vendors
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This will send emails to all eligible verified vendors who haven't opted out.
            </AlertDescription>
          </Alert>
          
          <div className="flex gap-3">
            <Button 
              onClick={() => runDigestMutation.mutate()}
              disabled={runDigestMutation.isPending}
            >
              {runDigestMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run Digest Now
            </Button>
            <Button variant="outline" onClick={() => setShowPreview(true)}>
              <Eye className="mr-2 h-4 w-4" />
              Preview Sample Email
            </Button>
          </div>
        </CardContent>
      </Card>

      <EmailPreviewModal 
        open={showPreview} 
        onOpenChange={setShowPreview}
        templateType="rfq_digest"
      />
    </div>
  );
}
